import { Controller } from "@hotwired/stimulus";

const ONE_MEGABYTE = 1024 * 1024;
const MIME_TYPE_LABELS = {
  "image/jpeg": "JPEG",
  "image/png": "PNG",
  "image/webp": "WEBP",
  "image/heic": "HEIC",
  "image/heif": "HEIF"
};
const HEIC_MIME_TYPES = new Set(["image/heic", "image/heif"]);

export default class extends Controller {
  static targets = ["input", "grid", "error", "dropzone"];

  static values = {
    allowedTypes: String,
    maxSizeBytes: Number,
    maxFiles: Number,
    existingCount: Number,
    orderInputName: String,
    previewAlt: String,
    mainBadgeLabel: String,
    photoManager: Boolean
  };

  connect() {
    this.objectUrls = [];
    this.pendingItems = [];
    this.pendingItemByToken = new Map();
    this.skipNextInputUpdate = false;
    this.clearPendingCards();
    this.clearDropzoneState();
  }

  disconnect() {
    this.revokeObjectUrls();
  }

  update() {
    if (this.skipNextInputUpdate) {
      this.skipNextInputUpdate = false;
      return;
    }

    this.clearErrors();
    this.ingestFiles(Array.from(this.inputTarget.files || []));
  }

  drop(event) {
    event.preventDefault();
    this.clearErrors();
    this.clearDropzoneState();
    this.ingestFiles(Array.from(event.dataTransfer?.files || []), { triggerInputChange: true });
  }

  dragEnter() {
    this.setDropzoneState(true);
  }

  dragOver(event) {
    event.preventDefault();
    this.setDropzoneState(true);
  }

  dragLeave(_event) {
    this.clearDropzoneState();
  }

  ingestFiles(files, { triggerInputChange = false } = {}) {
    if (files.length === 0) {
      this.syncPendingFiles();
      return;
    }

    const { validFiles: validatedFiles, rejectedMessages } = this.validateFiles(files);
    const validFiles = this.filesWithinLimit(validatedFiles, rejectedMessages);
    if (rejectedMessages.length > 0) {
      this.renderErrors(rejectedMessages);
    }

    if (!this.supportsDataTransfer()) {
      return;
    }

    if (validFiles.length === 0) {
      this.syncPendingFiles();
      return;
    }

    if (this.singleFileMode()) {
      this.clearPendingCards();
    }

    const newItems = validFiles.map((file) => this.buildPendingItem(file));
    this.pendingItems = [ ...this.pendingItems, ...newItems ];
    newItems.forEach((item) => this.pendingItemByToken.set(item.token, item));
    this.appendPendingItems(newItems);
    this.syncPendingFiles();

    if (triggerInputChange) {
      this.skipNextInputUpdate = true;
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  filesWithinLimit(files, rejectedMessages) {
    if (!this.hasMaxFilesValue || !Number.isFinite(this.maxFilesValue) || this.maxFilesValue < 1) return files;

    if (this.singleFileMode()) {
      if (files.length <= 1) return files;

      rejectedMessages.push("Only one photo can be selected here. The first supported photo was kept.");
      return files.slice(0, 1);
    }

    const availableSlots = this.availableSlots();
    if (files.length <= availableSlots) return files;

    if (availableSlots === 0) {
      rejectedMessages.push(
        `You can add up to ${this.maxFilesValue} photos to a preparation. Remove an existing photo before adding another.`
      );
    } else {
      rejectedMessages.push(
        `You can add up to ${this.maxFilesValue} photos to a preparation. Only the first ${availableSlots} selected photos were kept.`
      );
    }
    return files.slice(0, availableSlots);
  }

  singleFileMode() {
    return this.hasMaxFilesValue && this.maxFilesValue === 1;
  }

  availablePendingSlots() {
    if (!this.hasMaxFilesValue || !Number.isFinite(this.maxFilesValue) || this.maxFilesValue < 1) return Infinity;

    const existingCount = this.activeExistingImageCount() +
      (this.hasExistingCountValue ? this.existingCountValue : 0);
    return Math.max(this.maxFilesValue - existingCount, 0);
  }

  availableSlots() {
    return Math.max(this.availablePendingSlots() - this.pendingCardsInDomOrder().length, 0);
  }

  reconcilePendingCapacity() {
    if (!this.hasMaxFilesValue || !Number.isFinite(this.maxFilesValue) || this.maxFilesValue < 1) {
      this.syncPendingFiles();
      return;
    }

    const pendingCards = this.pendingCardsInDomOrder();
    const excessCards = pendingCards.slice(this.availablePendingSlots());

    if (excessCards.length > 0) {
      excessCards.forEach((card) => this.removePendingCard(card));
      const removedLabel = excessCards.length === 1 ? "the pending replacement photo" : "the newest pending photos";
      this.renderErrors([
        `Restoring that photo removed ${removedLabel} to keep this preparation within ${this.maxFilesValue} photos.`
      ]);
    }

    this.syncPendingFiles();
  }

  activeExistingImageCount() {
    if (!this.hasGridTarget) return 0;

    return Array.from(
      this.gridTarget.querySelectorAll("[data-preparation-image-kind='existing']")
    ).filter((card) => {
      const removeInput = card.querySelector("[data-preparation-images-target='removeInput']");
      return !removeInput || removeInput.disabled;
    }).length;
  }

  remove(event) {
    event.preventDefault();

    const card = event.currentTarget.closest("[data-preparation-image-kind='pending']");
    if (!card) return;

    this.removePendingCard(card);
    this.syncPendingFiles();
    this.dispatchGridChanged();
  }

  allowedTypes() {
    if (!this.hasAllowedTypesValue || this.allowedTypesValue.trim() === "") return [];

    return this.allowedTypesValue
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }

  validateFiles(files) {
    const validFiles = [];
    const rejectedMessages = [];
    const allowedTypes = this.allowedTypes();
    const maxSizeBytes = this.maxSizeBytesValue || 10 * ONE_MEGABYTE;

    files.forEach((file) => {
      if (!this.allowedFileType(file, allowedTypes)) {
        rejectedMessages.push(
          `${file.name} isn't a supported image type (${this.supportedTypeLabel(allowedTypes)}).`
        );
        return;
      }

      if (file.size > maxSizeBytes) {
        rejectedMessages.push(`${file.name} is too large (max ${this.megabytes(maxSizeBytes)} MB).`);
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, rejectedMessages };
  }

  allowedFileType(file, allowedTypes) {
    const mimeType = file.type.toLowerCase();
    if (allowedTypes.includes(mimeType)) return true;

    const previewFormat = this.heicPreviewFormat(file);
    return previewFormat ? allowedTypes.includes(`image/${previewFormat.toLowerCase()}`) : false;
  }

  syncPendingFiles() {
    if (!this.hasGridTarget || !this.hasInputTarget || !this.supportsDataTransfer()) return;

    const pendingCards = this.pendingCardsInDomOrder();
    const orderedTokens = pendingCards
      .map((card) => card.dataset.preparationImageOrderToken)
      .filter(Boolean);
    const dataTransfer = new DataTransfer();

    this.pendingItems = orderedTokens
      .map((token) => this.pendingItemByToken.get(token))
      .filter(Boolean);

    orderedTokens.forEach((token) => {
      const item = this.pendingItemByToken.get(token);
      if (item) {
        dataTransfer.items.add(item.file);
      }
    });

    this.inputTarget.files = dataTransfer.files;
    this.updateGridVisibility();
  }

  clearPendingCards() {
    if (this.hasGridTarget) {
      this.pendingCardsInDomOrder().forEach((card) => {
        const token = card.dataset.preparationImageOrderToken;
        const item = token ? this.pendingItemByToken.get(token) : null;
        if (item) {
          this.revokeObjectUrl(item.objectUrl);
        }

        card.remove();
      });
    }

    this.pendingItems = [];
    this.pendingItemByToken.clear();
    this.revokeObjectUrls();
    this.syncPendingFiles();
  }

  appendPendingItems(items) {
    if (!this.hasGridTarget) return;

    items.forEach((item) => {
      this.gridTarget.appendChild(this.buildPendingCard(item));
    });

    this.updateGridVisibility();
    this.dispatchGridChanged();
  }

  buildPendingItem(file) {
    const previewFormat = this.heicPreviewFormat(file);
    const objectUrl = previewFormat ? null : URL.createObjectURL(file);
    if (objectUrl) this.objectUrls.push(objectUrl);

    return {
      file,
      objectUrl,
      previewFormat,
      token: this.generateToken()
    };
  }

  buildPendingCard(item) {
    const managerLayout = this.hasPhotoManagerValue && this.photoManagerValue;
    const card = document.createElement("li");
    card.id = `preparation-image-pending-${item.token}`;
    card.className = managerLayout
      ? "preparation-photo-card preparation-photo-card--manager w-full"
      : "preparation-photo-card w-full";
    card.dataset.preparationImageKind = "pending";
    card.dataset.preparationImageOrderToken = item.token;
    card.dataset.preparationImageFilename = item.file.name;
    card.setAttribute("data-preparation-images-target", "card");

    const surface = document.createElement("article");
    surface.className = managerLayout
      ? "preparation-photo-card__surface preparation-photo-card__surface--manager"
      : "preparation-photo-card__surface";

    const orderInput = this.buildOrderInput(item);

    const photoShell = document.createElement("div");
    photoShell.className = "preparation-photo-card__photo-shell";

    const media = document.createElement("div");
    media.className = "aspect-[4/3] overflow-hidden";

    const preview = item.previewFormat
      ? this.buildHeicPreviewPlaceholder(item.previewFormat)
      : this.buildImagePreview(item.objectUrl);

    const mainBadge = document.createElement("span");
    mainBadge.className = "hidden preparation-photo-card__main-badge";
    mainBadge.textContent = this.hasMainBadgeLabelValue ? this.mainBadgeLabelValue : "Main image";
    mainBadge.setAttribute("data-preparation-images-target", "mainBadge");

    const newBadge = document.createElement("span");
    newBadge.className = "preparation-photo-card__preview-badge";
    newBadge.textContent = "New";
    newBadge.setAttribute("data-preparation-images-target", "newBadge");

    const removalBadge = document.createElement("span");
    removalBadge.className = "hidden preparation-photo-card__removal-badge";
    removalBadge.setAttribute("aria-live", "polite");
    removalBadge.textContent = "Will be removed";
    removalBadge.setAttribute("data-preparation-images-target", "removalBadge");

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = managerLayout
      ? "preparation-photo-card__remove-action"
      : "preparation-photo-card__icon-button preparation-photo-card__icon-button--light preparation-photo-card__remove-button border-(--color-ec-danger)/30 text-ec-danger hover:bg-ec-danger/5";
    removeButton.setAttribute("aria-label", `Remove ${item.file.name}`);
    removeButton.setAttribute("aria-pressed", "false");
    removeButton.setAttribute("data-action", "click->image-previews#remove");
    removeButton.setAttribute("data-preparation-images-target", "removeButton");
    removeButton.textContent = managerLayout ? "Remove" : "×";

    const dragHandle = document.createElement("button");
    dragHandle.type = "button";
    dragHandle.className = managerLayout
      ? "preparation-photo-card__drag-action"
      : "preparation-photo-card__icon-button preparation-photo-card__icon-button--tint preparation-photo-card__drag-handle";
    dragHandle.setAttribute("aria-label", managerLayout ? `Drag to reorder ${item.file.name}` : "Drag to reorder photo");
    dragHandle.setAttribute("data-preparation-images-target", "dragHandle");
    dragHandle.textContent = managerLayout ? "⠿ Drag" : "⋮⋮";

    const footer = document.createElement("div");
    footer.className = managerLayout
      ? "preparation-photo-card__footer"
      : "flex flex-1 flex-col border-t border-ec-border px-3 py-3";

    const reorderControls = document.createElement("div");
    reorderControls.className = managerLayout
      ? "preparation-photo-card__reorder-controls"
      : "mt-4 flex items-center justify-between gap-2";
    reorderControls.setAttribute("data-preparation-images-target", "reorderControls");

    const moveEarlierButton = document.createElement("button");
    moveEarlierButton.type = "button";
    moveEarlierButton.className = managerLayout
      ? "preparation-photo-card__move-button"
      : "preparation-photo-card__reorder-button";
    moveEarlierButton.setAttribute("aria-label", `Move ${item.file.name} earlier`);
    moveEarlierButton.setAttribute("data-action", "click->preparation-images#moveEarlier");
    moveEarlierButton.setAttribute("data-preparation-images-target", "moveEarlierButton");
    moveEarlierButton.textContent = "←";

    const moveLaterButton = document.createElement("button");
    moveLaterButton.type = "button";
    moveLaterButton.className = managerLayout
      ? "preparation-photo-card__move-button"
      : "preparation-photo-card__reorder-button";
    moveLaterButton.setAttribute("aria-label", `Move ${item.file.name} later`);
    moveLaterButton.setAttribute("data-action", "click->preparation-images#moveLater");
    moveLaterButton.setAttribute("data-preparation-images-target", "moveLaterButton");
    moveLaterButton.textContent = "→";

    const moveEarlierWrap = document.createElement("div");
    moveEarlierWrap.className = "flex items-center justify-start";
    moveEarlierWrap.appendChild(moveEarlierButton);

    const moveLaterWrap = document.createElement("div");
    moveLaterWrap.className = "flex items-center justify-end";
    moveLaterWrap.appendChild(moveLaterButton);

    if (managerLayout) reorderControls.appendChild(dragHandle);
    reorderControls.appendChild(moveEarlierWrap);
    reorderControls.appendChild(moveLaterWrap);

    media.appendChild(preview);
    if (!this.singleFileMode()) media.appendChild(mainBadge);
    if (!managerLayout) media.appendChild(newBadge);
    if (!this.singleFileMode()) media.appendChild(removalBadge);
    if (!managerLayout) media.appendChild(removeButton);
    if (!managerLayout && !this.singleFileMode()) media.appendChild(dragHandle);

    photoShell.appendChild(media);
    if (managerLayout) {
      const metadata = document.createElement("div");
      metadata.className = "preparation-photo-card__metadata";

      const filename = document.createElement("p");
      filename.className = "preparation-photo-card__filename";
      filename.textContent = item.file.name;

      const status = document.createElement("p");
      status.className = "preparation-photo-card__file-status";
      status.textContent = item.previewFormat
        ? `${item.previewFormat} · Preview after save`
        : "Ready to add";

      const actions = document.createElement("div");
      actions.className = "preparation-photo-card__actions";
      if (!this.singleFileMode()) actions.appendChild(reorderControls);
      actions.appendChild(removeButton);

      metadata.appendChild(filename);
      metadata.appendChild(status);
      footer.appendChild(metadata);
      footer.appendChild(actions);
    } else if (item.previewFormat) {
      if (!this.singleFileMode()) footer.appendChild(reorderControls);
      const selectionCopy = document.createElement("p");
      selectionCopy.className = "mb-0 text-xs font-semibold text-ec-text";
      selectionCopy.textContent = `${item.previewFormat} photo selected`;

      const filename = document.createElement("p");
      filename.className = "mt-1 mb-0 break-all text-xs text-ec-muted-text";
      filename.textContent = item.file.name;

      const previewCopy = document.createElement("p");
      previewCopy.className = "mt-1 mb-0 text-xs text-ec-muted-text";
      previewCopy.textContent = "Preview available after save.";

      footer.appendChild(selectionCopy);
      footer.appendChild(filename);
      footer.appendChild(previewCopy);
    } else {
      if (!this.singleFileMode()) footer.appendChild(reorderControls);
      const pendingCopy = document.createElement("p");
      pendingCopy.className = "mt-2 mb-0 text-xs text-ec-muted-text";
      pendingCopy.textContent = "added when you save.";
      footer.appendChild(pendingCopy);
    }
    if (orderInput) surface.appendChild(orderInput);
    surface.appendChild(photoShell);
    surface.appendChild(footer);
    card.appendChild(surface);

    return card;
  }

  buildOrderInput(item) {
    if (!this.hasOrderInputNameValue || this.orderInputNameValue.trim() === "") return null;

    const orderInput = document.createElement("input");
    orderInput.type = "hidden";
    orderInput.name = this.orderInputNameValue;
    orderInput.value = item.token;
    orderInput.setAttribute("data-preparation-images-target", "orderInput");

    return orderInput;
  }

  buildImagePreview(objectUrl) {
    const image = document.createElement("img");
    image.src = objectUrl;
    image.alt = this.hasPreviewAltValue ? this.previewAltValue : "Photo preview";
    image.className = "h-full w-full object-contain p-3 sm:p-4";
    image.loading = "lazy";
    image.decoding = "async";
    image.sizes = this.hasPhotoManagerValue && this.photoManagerValue
      ? "(min-width: 1024px) 300px, (min-width: 640px) calc((100vw - 4rem) / 2), calc(100vw - 3rem)"
      : "(min-width: 1280px) 360px, (min-width: 1024px) 320px, (min-width: 640px) calc((100vw - 5rem) / 3), calc((100vw - 2rem) / 2)";

    return image;
  }

  buildHeicPreviewPlaceholder(format) {
    const placeholder = document.createElement("div");
    placeholder.className = "flex h-full w-full items-center justify-center bg-ec-soft-surface px-12 text-center";
    placeholder.setAttribute("data-heic-preview-placeholder", "");

    const formatLabel = document.createElement("span");
    formatLabel.className = "rounded-full border border-ec-border bg-ec-page px-3 py-1 text-xs font-semibold text-ec-muted-text";
    formatLabel.textContent = format;
    placeholder.appendChild(formatLabel);

    return placeholder;
  }

  pendingCardsInDomOrder() {
    if (!this.hasGridTarget) return [];

    return Array.from(
      this.gridTarget.querySelectorAll("[data-preparation-image-kind='pending']")
    );
  }

  removePendingCard(card) {
    const token = card.dataset.preparationImageOrderToken;
    const item = token ? this.pendingItemByToken.get(token) : null;

    if (item) {
      this.pendingItems = this.pendingItems.filter((pendingItem) => pendingItem.token !== token);
      this.pendingItemByToken.delete(token);
      this.revokeObjectUrl(item.objectUrl);
    }

    card.remove();
    this.updateGridVisibility();
  }

  updateGridVisibility() {
    if (!this.hasGridTarget) return;

    const hasCards = this.gridTarget.querySelectorAll("[data-preparation-images-target='card']").length > 0;
    this.gridTarget.classList.toggle("hidden", !hasCards);

    if (this.singleFileMode()) {
      const hasPendingCard = this.pendingCardsInDomOrder().length > 0;
      this.gridTarget.querySelectorAll("[data-image-previews-existing-card]").forEach((card) => {
        card.classList.toggle("hidden", hasPendingCard);
        card.setAttribute("aria-hidden", hasPendingCard ? "true" : "false");
      });
    }
  }

  dispatchGridChanged() {
    if (!this.hasGridTarget) return;

    this.gridTarget.dispatchEvent(new CustomEvent("preparation-images:changed", {
      bubbles: true
    }));
  }

  setDropzoneState(active) {
    if (!this.hasDropzoneTarget) return;

    this.dropzoneTarget.classList.toggle("border-link", active);
    this.dropzoneTarget.classList.toggle("bg-(--color-ec-soft-surface)", active);
    this.dropzoneTarget.classList.toggle("shadow-md", active);
  }

  clearDropzoneState() {
    this.setDropzoneState(false);
  }

  revokeObjectUrl(objectUrl) {
    if (!objectUrl) return;

    URL.revokeObjectURL(objectUrl);
    this.objectUrls = this.objectUrls.filter((candidate) => candidate !== objectUrl);
  }

  revokeObjectUrls() {
    this.objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    this.objectUrls = [];
  }

  generateToken() {
    if (typeof globalThis.crypto !== "undefined" && typeof globalThis.crypto.randomUUID === "function") {
      return globalThis.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  supportsDataTransfer() {
    return typeof DataTransfer !== "undefined";
  }

  megabytes(bytes) {
    return (bytes / ONE_MEGABYTE).toFixed(0);
  }

  supportedTypeLabel(allowedTypes) {
    if (allowedTypes.length === 0) {
      return "JPEG, PNG, WEBP";
    }

    return allowedTypes
      .map((type) => MIME_TYPE_LABELS[type] || type.split("/").pop().toUpperCase())
      .join(", ");
  }

  heicPreviewFormat(file) {
    const mimeType = file.type.toLowerCase();
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension === "heic") return "HEIC";
    if (extension === "heif") return "HEIF";
    if (mimeType === "image/heif") return "HEIF";
    if (HEIC_MIME_TYPES.has(mimeType)) return "HEIC";

    return null;
  }

  clearErrors() {
    this.errorTarget.innerHTML = "";
    this.errorTarget.classList.add("hidden");
    this.inputTarget.removeAttribute("aria-invalid");
  }

  renderErrors(messages) {
    if (messages.length === 0) return;

    this.errorTarget.innerHTML = "";

    if (messages.length === 1) {
      this.errorTarget.textContent = messages[0];
    } else {
      const list = document.createElement("ul");
      list.className = "list-disc pl-5";

      messages.forEach((message) => {
        const item = document.createElement("li");
        item.textContent = message;
        list.appendChild(item);
      });

      this.errorTarget.appendChild(list);
    }

    this.errorTarget.classList.remove("hidden");
    this.inputTarget.setAttribute("aria-invalid", "true");
  }
}
